import { Component, OnInit } from '@angular/core';
import { CurrencyPipe, DatePipe, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';

type UrgencyBand = 'all' | 'fresh' | 'aging' | 'critical';

interface NotificationItem {
  id: string | number;
  patient_id: number;
  patient_name: string;
  due_amount: number;
  reason: string;
  follow_up_date: string | null;
  prescription_id: number | null;
  consultation_date: string | null;
  days_pending: number;
}

@Component({
  standalone: true,
  selector: 'app-reception-dues',
  imports: [
    TableModule,
    DatePipe,
    CurrencyPipe,
    CommonModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
  ],
  templateUrl: './reception-dues.component.html',
  styleUrl: './reception-dues.component.scss'
})
export class ReceptionDuesComponent implements OnInit {
  readonly graceDays = 15;

  notifications: NotificationItem[] = [];
  filteredNotifications: NotificationItem[] = [];
  loading = false;
  searchTerm = '';
  activeBand: UrgencyBand = 'all';

  constructor(
    private router: Router,
    private patientRegistrationService: PatientRegistrationService
  ) {}

  ngOnInit(): void {
    this.loadNotifications();
  }

  loadNotifications(): void {
    this.loading = true;

    this.patientRegistrationService.getDuesData().subscribe({
      next: (res: any) => {
        const rawRows = Array.isArray(res?.data) ? res.data : [];
        this.notifications = rawRows
          .map((item: any) => this.normalizeNotification(item))
          .sort((a: NotificationItem, b: NotificationItem) =>
            b.days_pending - a.days_pending || b.due_amount - a.due_amount
          );
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  refreshNotifications(): void {
    this.loadNotifications();
  }

  setBand(band: UrgencyBand): void {
    this.activeBand = band;
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
  }

  clearFilters(): void {
    this.activeBand = 'all';
    this.searchTerm = '';
    this.applyFilters();
  }

  onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.applyFilters();
  }

  openDuesSettlement(notification: NotificationItem): void {
    this.router.navigate(
      ['/admin/patient-registration'],
      {
        queryParams: {
          patientId: notification.patient_id,
          prescriptionId: notification.prescription_id,
          dueAmount: notification.due_amount,
          openDues: true
        }
      }
    );
  }

  get totalDuesAmount(): number {
    return this.filteredNotifications.reduce((sum, item) => sum + (Number(item.due_amount) || 0), 0);
  }

  get oldestPendingDays(): number {
    if (!this.filteredNotifications.length) return 0;
    return Math.max(...this.filteredNotifications.map(item => item.days_pending || 0));
  }

  get highestDueAmount(): number {
    if (!this.filteredNotifications.length) return 0;
    return Math.max(...this.filteredNotifications.map(item => Number(item.due_amount) || 0));
  }

  get bandCounts(): Record<UrgencyBand, number> {
    return {
      all: this.notifications.length,
      fresh: this.notifications.filter(n => n.days_pending < 30).length,
      aging: this.notifications.filter(n => n.days_pending >= 30 && n.days_pending < 60).length,
      critical: this.notifications.filter(n => n.days_pending >= 60).length,
    };
  }

  urgencyLevel(days: number): 'fresh' | 'aging' | 'critical' {
    if (days >= 60) return 'critical';
    if (days >= 30) return 'aging';
    return 'fresh';
  }

  patientInitials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private applyFilters(): void {
    const query = this.searchTerm.trim().toLowerCase();

    this.filteredNotifications = this.notifications.filter(item => {
      const matchesBand =
        this.activeBand === 'all' ||
        (this.activeBand === 'fresh' && item.days_pending < 30) ||
        (this.activeBand === 'aging' && item.days_pending >= 30 && item.days_pending < 60) ||
        (this.activeBand === 'critical' && item.days_pending >= 60);

      if (!matchesBand) return false;
      if (!query) return true;

      return (
        item.patient_name.toLowerCase().includes(query) ||
        item.reason.toLowerCase().includes(query)
      );
    });
  }

  private normalizeNotification(item: any): NotificationItem {
    const data = item?.data || {};
    const consultationDate = String(
      item?.created_at ?? data?.created_at ?? item?.consultation_date ?? ''
    ).trim() || null;

    const daysFromApi = Number(item?.days_pending ?? data?.days_pending);
    const daysPending = Number.isFinite(daysFromApi) && daysFromApi >= 0
      ? Math.floor(daysFromApi)
      : this.daysSince(consultationDate);

    return {
      id: item?.id ?? data?.id ?? '',
      patient_id: Number(item?.patient_id ?? data?.patient_id ?? 0),
      patient_name: String(item?.patient_name ?? data?.patient_name ?? '').trim(),
      due_amount: Number(item?.due_amount ?? data?.due_amount ?? 0),
      reason: String(item?.reason ?? data?.reason ?? 'Personal Due').trim(),
      follow_up_date: item?.follow_up_date ?? data?.follow_up_date ?? null,
      prescription_id: Number(item?.prescription_id ?? data?.prescription_id ?? 0) || null,
      consultation_date: consultationDate,
      days_pending: daysPending,
    };
  }

  private daysSince(dateValue: string | null): number {
    if (!dateValue) return 0;
    const start = this.toDayStart(new Date(dateValue));
    const today = this.toDayStart(new Date());
    if (Number.isNaN(start.getTime())) return 0;
    return Math.max(0, Math.floor((today.getTime() - start.getTime()) / 86400000));
  }

  private toDayStart(value: Date): Date {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }
}
